import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PayoutStatement, ShopPayout } from '@popcart/core/v1';
import { api, errorMessage, errorStatus } from '../../api';
import { ConfirmDialog, EmptyState, ErrorState, Loading, StatusPill, useTitle } from '../../ui/bits';
import { Fig, PageHead, RowMenu, Stat } from '../../ui/viz';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { STATE_ART } from '../../lib/voice.js';
import { count, peso } from '../../lib/format.js';
import { DialogActions, Modal, StaleNote, TextField } from '../payments/kit';
import { Dot, Hint, SplitBar } from '../payments/money';

import { PeriodPicker } from '../reports/PeriodPicker';
import { lastWeek, manilaStamp, periodLabel, thisWeek, type Period } from '../reports/period.js';
import { PAYOUT_PILL, REFERENCE_MAX, commissionRateLabel, createPayoutAction, markPaidAction, payoutMethodLabel, payoutsKey, sortPayouts, statementRows, statementTotals, withPayout } from './lib.js';

type Data = Awaited<ReturnType<typeof api.opsPayouts>>;

/**
 * /ops/payouts: what each partner shop earned in a period (gross − commission ± adjustments),
 * "Create payout" to freeze it, then mark it paid with the transfer reference (or void it).
 * The period defaults to last week, Monday to Sunday, Manila time. Only the next thing to do
 * (the first row with an action) gets the primary button.
 */
export function OpsPayoutsPage() {
  useTitle('Payouts');
  const qc = useQueryClient();
  const toast = useToast();
  const [period, setPeriod] = useState<Period>(() => lastWeek());
  const [paying, setPaying] = useState<ShopPayout | null>(null);
  const [voiding, setVoiding] = useState<ShopPayout | null>(null);
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const key = payoutsKey(period);
  const q = useQuery({ queryKey: key, queryFn: () => api.opsPayouts(period), placeholderData: keepPreviousData });
  const data = q.data;

  const put = (p: ShopPayout) => qc.setQueryData<Data>(key, (old) => (old ? withPayout(old, p) : old));

  const create = useMutation({
    mutationFn: (s: PayoutStatement) => createPayoutAction(api, s.shopId, period),
    onMutate: (s) => setCreatingId(s.shopId),
    onSuccess: (p) => {
      put(p);
      toast(`${peso(p.netCentavos)} payout for ${p.shopName} is ready to pay.`);
    },
    onError: (err, s) => {
      if (errorStatus(err) === 409) toast(`${s.shopName} already has a payout for this period.`, 'error');
      else toast(errorMessage(err, "Couldn't create the payout. Try again."), 'error');
    },
    onSettled: () => {
      setCreatingId(null);
      void qc.invalidateQueries({ queryKey: ['ops', 'payouts'] });
    },
  });

  const voidIt = useMutation({
    mutationFn: (p: ShopPayout) => api.voidPayout(p.id),
    onSuccess: (p) => {
      put(p);
      setVoiding(null);
      toast(`The ${p.shopName} payout is void. Create it again when the numbers are right.`);
      void qc.invalidateQueries({ queryKey: ['ops', 'payouts'] });
    },
    onError: (err) => toast(errorMessage(err, "Couldn't void the payout. Try again."), 'error'),
  });

  const rows = data ? statementRows(data.statements, data.payouts, { from: data.from, to: data.to }) : [];
  const totals = data ? statementTotals(data.statements) : null;
  const toPay = rows.filter((r) => r.next === 'pay').length;
  const toCreate = rows.filter((r) => r.next === 'create').length;
  const paidNet = rows.filter((r) => r.next === 'paid').reduce((s, r) => s + r.statement.netCentavos, 0);
  const firstAction = rows.find((r) => r.next === 'create' || r.next === 'pay')?.statement.shopId ?? null;
  const commissionPct = totals && totals.grossCentavos > 0 ? Math.round((totals.commissionCentavos / totals.grossCentavos) * 100) : null;

  return (
    <div className="mn-page">
      <PageHead
        title="Payouts"
        meta={
          <>
            Partner shops
            {data && toPay ? <span className="dv-chip">{count(toPay)} to pay</span> : null}
            {data && toCreate ? <span className="dv-chip">{count(toCreate)} to create</span> : null}
          </>
        }
      />

      <section className="card mn-bar dv-rise" aria-label="Period" style={rise(0)}>
        <PeriodPicker
          idPrefix="po-period"
          label="Period"
          value={period}
          onChange={setPeriod}
          presets={[
            { id: 'last', label: 'Last week', period: lastWeek() },
            { id: 'this', label: 'This week', period: thisWeek() },
          ]}
        />
      </section>

      {q.isError && !data ? (
        <section className="card">
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </section>
      ) : !data || !totals ? (
        <Loading label="Loading payouts" rows={3} />
      ) : (
        <div className="mn-page" aria-busy={q.isFetching || undefined}>
          <StaleNote show={q.isError} what="payouts" />
          <section aria-label="Totals" className="mn-stats4 dv-rise" style={rise(1)}>
            <Stat label="Gross" value={peso(totals.grossCentavos)} sub={`${count(totals.ordersCount)} delivered orders`} />
            <Stat label="Commission" value={peso(totals.commissionCentavos)} sub={commissionPct ? `${commissionPct}% of gross` : 'PopCart keeps'} />
            <Stat label="Adjustments" value={peso(totals.adjustmentsCentavos)} sub="Catch-weight, fixes" />
            <Stat
              label="To partners"
              tone="ink"
              value={peso(totals.netCentavos)}
              sub={paidNet ? `${peso(paidNet)} paid · ${count(rows.length)} shops` : `${count(rows.length)} shops`}
              visual={<SplitBar net={totals.netCentavos} commission={totals.commissionCentavos} adjustments={totals.adjustmentsCentavos} />}
            />
          </section>

          <section className="card dv-card dv-rise" aria-labelledby="po-st-h" style={rise(2)}>
            <div className="dv-card-h">
              <h2 id="po-st-h">Statements</h2>
              <span className="dv-chip">{count(rows.length)}</span>
              <Hint text="The same statement each shop sees on its Sales page. Create the payout to freeze it, transfer the money, then mark it paid." />
              <span className="grow" />
              <span className="mn-key" aria-hidden="true">
                <span>
                  <i className="net" />
                  Net
                </span>
                <span>
                  <i className="com" />
                  Commission
                </span>
                <span>
                  <i className="adj" />
                  Adjustments
                </span>
              </span>
            </div>
            {rows.length === 0 ? (
              <EmptyState art={STATE_ART.noOrders} title="No partner sales in this period">
                Shops show up once they have delivered orders.
              </EmptyState>
            ) : (
              <div className="table-wrap mn-table">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Shop</th>
                      <th scope="col" className="num">
                        Orders
                      </th>
                      <th scope="col" className="num">
                        Gross
                      </th>
                      <th scope="col" className="num">
                        Commission
                      </th>
                      <th scope="col" className="num">
                        Adjustments
                      </th>
                      <th scope="col" className="num">
                        Net
                      </th>
                      <th scope="col">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ statement: s, payout, next }) => {
                      const main = s.shopId === firstAction ? 'btn btn-primary' : 'btn btn-secondary';
                      return (
                        <tr key={s.shopId} className={next === 'pay' ? 'ops-flag' : undefined}>
                          <td>
                            <span className="mn-who">
                              <Dot tone={next === 'paid' ? 'ok' : next === 'pay' ? 'warn' : next === 'create' ? 'info' : 'off'} title={next === 'paid' ? 'Paid' : next === 'pay' ? 'To pay' : next === 'create' ? 'To create' : 'Nothing to pay'} />
                              <span className="stack">
                                <span className="t">{s.shopName}</span>
                                <span className={s.payoutMethod ? 'mn-cap' : 'mn-cap bad'}>{payoutMethodLabel(s.payoutMethod)}</span>
                              </span>
                            </span>
                          </td>
                          <td className="num">{count(s.ordersCount)}</td>
                          <td className="num">{peso(s.grossCentavos)}</td>
                          <td className="num">
                            {peso(s.commissionCentavos)}
                            <div className="mn-cap">{commissionRateLabel(s)}</div>
                          </td>
                          <td className="num">{peso(s.adjustmentsCentavos)}</td>
                          <td className="num">
                            <div className="stack s4" style={{ alignItems: 'flex-end' }}>
                              <Fig text={peso(s.netCentavos)} size="sm" animate={false} />
                              <SplitBar net={s.netCentavos} commission={s.commissionCentavos} adjustments={s.adjustmentsCentavos} />
                            </div>
                          </td>
                          <td className="num">
                            <div className="actions">
                              {next === 'create' ? (
                                <button type="button" className={main} disabled={create.isPending} aria-busy={creatingId === s.shopId || undefined} onClick={() => create.mutate(s)} aria-label={`Create the payout for ${s.shopName}`}>
                                  <Icon name="plus" />
                                  {creatingId === s.shopId ? 'Creating…' : 'Create payout'}
                                </button>
                              ) : next === 'pay' && payout ? (
                                <button type="button" className={main} onClick={() => setPaying(payout)} aria-label={`Mark the ${s.shopName} payout paid`}>
                                  <Icon name="check" />
                                  Mark paid
                                </button>
                              ) : next === 'paid' && payout ? (
                                <StatusPill tone="success">Paid</StatusPill>
                              ) : (
                                <span className="mn-cap">Nothing to pay</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card dv-card dv-rise" aria-labelledby="po-list-h" style={rise(3)}>
            <div className="dv-card-h">
              <h2 id="po-list-h">Payouts</h2>
              {data.payouts.length ? <span className="dv-chip">{count(data.payouts.length)}</span> : null}
            </div>
            {data.payouts.length === 0 ? (
              <div className="dv-quiet">No payouts for this period yet.</div>
            ) : (
              <ul className="mn-rows mn-po">
                {sortPayouts(data.payouts).map((p) => {
                  const pill = PAYOUT_PILL[p.status];
                  return (
                    <li key={p.id} className={p.status === 'void' ? 'mn-void' : undefined}>
                      <span className="l">
                        <Dot tone={p.status === 'paid' ? 'ok' : p.status === 'pending' ? 'warn' : 'off'} />
                        <span className="stack">
                          <span className="t">{p.shopName}</span>
                          <span className="mn-cap">
                            {periodLabel(p)} · {count(p.ordersCount)} orders · created {manilaStamp(p.createdAt)}
                          </span>
                        </span>
                      </span>
                      <span className="stack s4" style={{ alignItems: 'flex-end' }}>
                          <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
                          {p.status === 'paid' ? (
                            <span className="mn-cap">
                              Ref {p.reference ?? '—'} · {manilaStamp(p.paidAt)}
                            </span>
                          ) : null}
                      </span>
                      <span className="num">
                        <Fig text={peso(p.netCentavos)} size="sm" animate={false} />
                      </span>
                        {p.status === 'pending' ? (
                          <span className="mn-act">
                            <button type="button" className="btn btn-secondary" onClick={() => setPaying(p)} aria-label={`Mark the ${p.shopName} payout paid`}>
                              Mark paid
                            </button>
                            <RowMenu label={`More for the ${p.shopName} payout`} items={[{ label: 'Void payout', icon: 'close', tone: 'danger', onSelect: () => setVoiding(p) }]} />
                          </span>
                        ) : (
                          <span aria-hidden="true" />
                        )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      )}

      <PaidDialog
        payout={paying}
        onClose={() => setPaying(null)}
        onPaid={(p) => {
          put(p);
          void qc.invalidateQueries({ queryKey: ['ops', 'payouts'] });
        }}
      />
      <ConfirmDialog
        open={voiding !== null}
        title={`Void the ${voiding?.shopName ?? ''} payout?`}
        body={`${peso(voiding?.netCentavos ?? 0)} for ${voiding ? periodLabel(voiding) : ''} won't be paid. You can create it again for the same period.`}
        confirmLabel="Void payout"
        busy={voidIt.isPending}
        onConfirm={() => voiding && voidIt.mutate(voiding)}
        onClose={() => setVoiding(null)}
      />
    </div>
  );
}

const rise = (i: number) => ({ ['--i' as string]: i }) as CSSProperties;

function PaidDialog({ payout, onClose, onPaid }: { payout: ShopPayout | null; onClose(): void; onPaid(p: ShopPayout): void }) {
  const toast = useToast();
  const [ref, setRef] = useState('');
  const [err, setErr] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  useEffect(() => {
    setRef('');
    setErr(undefined);
  }, [payout?.id]);
  const save = useMutation({
    mutationFn: (v: { payout: ShopPayout; reference: string }) => markPaidAction(api, v.payout.id, v.reference),
    onSuccess: (r) => {
      if (!r.ok) {
        setErr(r.error);
        inputRef.current?.focus();
        return;
      }
      toast(`${r.payout.shopName} is paid ${peso(r.payout.netCentavos)}.`);
      onPaid(r.payout);
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (payout) save.mutate({ payout, reference: ref });
  };
  return (
    <Modal open={payout !== null} labelledBy="po-paid-title" onClose={onClose}>
      {payout ? (
        <form className="stack s24" onSubmit={submit} noValidate>
          <div className="stack s4">
            <h2 id="po-paid-title" className="t-headline">
              Mark {peso(payout.netCentavos)} to {payout.shopName} paid
            </h2>
            <span className="t-caption muted">
              {periodLabel(payout)} · {count(payout.ordersCount)} orders. Only once the transfer has gone through.
            </span>
          </div>
          <TextField id="po-paid-ref" label="Transfer reference" value={ref} onChange={setRef} error={err} maxLength={REFERENCE_MAX} placeholder="GCash or bank reference" help="The shop sees it on its Sales page." inputRef={inputRef} />
          {save.isError ? (
            <p className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
              {errorMessage(save.error, "Couldn't mark it paid. Try again.")}
            </p>
          ) : null}
          <DialogActions busy={save.isPending} busyLabel="Saving…" confirm="Mark paid" onCancel={onClose} />
        </form>
      ) : null}
    </Modal>
  );
}
